// Phase Offset (MDL-301, spec 132481027) — where in its wave an effect sits at
// the start of its cycle, in degrees.
//
// `phase_offset` is a number from -180 to +180, the same in Hz and clock-synced
// modes. Positive runs the wave ahead of its cycle: for a bounce, +90 starts
// falling, ±180 starts at the trough and -90 starts rising. -180 and +180 are the
// same point. Absent = 0, so there is no migration and a patch that never used
// the feature is unchanged; the knob writes 0 as an absent key.
//
// The knob clicks onto 0, ±90 and ±180 but takes any value between: the firmware
// accepts any number in range (app/lib/midulate/effect.py parse_phase_offset).
// Degrees are measured from the start of the cycle, so a ZigZag or Swell cannot
// say "start on my peak" and have it follow Skew; that is deliberate (MDL-420
// records the Mirror idea that would cover it).

export const PHASE_OFFSET_RANGE = 180;
export const PHASE_OFFSET_SNAPS = [-180, -90, 0, 90, 180];

// The device advertises `phase_offset` in its capabilities; older firmware omits
// it, and the knob stays hidden (no mixed state).
export function isPhaseOffsetSupported(capabilities) {
  const caps = capabilities?.capabilities ?? capabilities ?? {};
  return !!caps.phase_offset;
}

// Every effect has a phase except wander. A glitch fires at random in Hz mode, so
// only a clock-synced glitch (whose beat-affinity window moves) has anything to
// offset.
export function isPhaseOffsetVisible(effect, capabilities) {
  if (!effect || effect.type === 'wander') return false;
  if (effect.type === 'glitch' && effect.clock_sync !== true) return false;
  return isPhaseOffsetSupported(capabilities);
}

// A stored value outside the range, or not a number, reads as the nearest valid
// one (as the firmware clamps it), and a missing one as 0.
export function phaseOffsetValue(effect) {
  const v = effect?.phase_offset;
  if (typeof v !== 'number' || !Number.isFinite(v)) return 0;
  return Math.max(-PHASE_OFFSET_RANGE, Math.min(PHASE_OFFSET_RANGE, v));
}

// Write the choice in place. 0 is stored as an absent key.
export function setPhaseOffset(effect, value) {
  const v = Math.round(Number(value));
  if (!Number.isFinite(v) || v === 0) {
    delete effect.phase_offset;
  } else {
    effect.phase_offset = Math.max(-PHASE_OFFSET_RANGE, Math.min(PHASE_OFFSET_RANGE, v));
  }
}

export function phaseOffsetTip(effect) {
  if (effect?.type === 'glitch') {
    return 'Where in the beat glitches cluster, in degrees of a beat. Only matters with Beat Affinity set.';
  }
  if (effect?.clock_sync) {
    return "Where in its wave the effect sits at the start of its cycle, in degrees. "
      + "The accent follows the wave's start, so offsetting moves the accent within the bar.";
  }
  return 'Where in its wave the effect starts, in degrees. '
    + 'Two effects at the same rate stay this far apart.';
}
